import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { Button } from 'components/facets'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import Dropzone from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  formatDateToYYYYMMDD,
  PurchaseEvent,
  PurchaseEventPayload,
  SellerPayload,
  SUPPORTED_CURRENCIES,
} from 'shared'
import { APP_CURRENCY } from 'utils/constants'

const INITIAL_SELLER: SellerPayload = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  province: '',
  zipCode: '',
  country: '',
  vatNumber: '',
}

const INITIAL_PURCHASE_EVENT: PurchaseEventPayload = {
  lotNumber: '',
  quantity: undefined,
  price: undefined,
  purchaseDate: new Date(),
  withVat: false,
  currency: APP_CURRENCY[0].code,
}

export const PurchaseEventCreation = ({
  onLotCreated,
  tabName,
  initialQuantity,
  initialPrice,
}: {
  onLotCreated?: (createdLot: PurchaseEvent) => void
  tabName?: string
  initialQuantity?: number
  initialPrice?: number
}): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { handleCreatePurchaseEvent } = useFetchPurchaseEvents()

  const [seller, setSeller] = useState<SellerPayload>(INITIAL_SELLER)
  const [purchaseEvent, setPurchaseEvent] = useState<PurchaseEventPayload>({
    ...INITIAL_PURCHASE_EVENT,
    ...(tabName ? { lotNumber: tabName } : {}),
    quantity: initialQuantity,
    price: initialPrice,
  })

  const [sellerErrors, setSellerErrors] = useState<Record<string, string>>({})
  const [purchaseErrors, setPurchaseErrors] = useState<Record<string, string>>({})

  const [receiptFile, setReceiptFile] = useState<File | null>(null)

  const updateSeller = <K extends keyof SellerPayload>(key: K, value: string) => {
    setSeller((prev) => ({ ...prev, [key]: value }))
  }

  const updatePurchasesEvent = <K extends keyof PurchaseEventPayload>(
    key: K,
    value: string | number
  ) => {
    setPurchaseEvent((prev) => ({ ...prev, [key]: value }))
  }

  const validateSeller = (_seller: SellerPayload): boolean => {
    const errors: Record<string, string> = {}

    setSellerErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateSingleSellerField = <K extends keyof SellerPayload>(
    _key: K,
    _value: string
  ): string | null => {
    return null
  }

  const validateSingleLotField = <K extends keyof PurchaseEventPayload>(
    key: K,
    value: PurchaseEventPayload[K]
  ): string | null => {
    switch (key) {
      case 'lotNumber':
        if (typeof value !== 'string' || !value.trim()) return t('taxation.errors.missingLotNumber')
        if (value.length < 2) return t('taxation.errors.wrongLotNumber')
        return null

      case 'price':
        if (value != null && (typeof value !== 'number' || isNaN(value)))
          return t('taxation.errors.invalidNumber')
        if (typeof value === 'number' && value < 0) return t('taxation.errors.invalidPrice')
        return null

      case 'quantity':
        if (value != null && (typeof value !== 'number' || isNaN(value)))
          return t('taxation.errors.invalidNumber')
        if (typeof value === 'number' && value < 0) return t('taxation.errors.invalidQuantity')
        if (typeof value === 'number' && !Number.isInteger(value))
          return t('taxation.errors.quantityInteger')
        return null

      case 'purchaseDate':
        if (!(value instanceof Date) || isNaN(value.getTime()))
          return t('taxation.errors.invalidDate')
        const today = new Date()
        if (value > today) return t('taxation.errors.invalidPurchaseDate')
        return null

      case 'currency':
        if (typeof value !== 'string' || !value.trim()) return t('taxation.errors.currencyRequired')
        return null

      default:
        return null
    }
  }

  const validatePurchaseEvent = (pe: PurchaseEventPayload): boolean => {
    const errors: Record<string, string> = {}

    if (!pe.lotNumber.trim()) errors.lotNumber = t('taxation.errors.missingLotNumber')
    if (!pe.purchaseDate || isNaN(new Date(pe.purchaseDate).getTime()))
      errors.purchaseDate = t('taxation.errors.invalidDate')

    setPurchaseErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveLot = async () => {
    if (!validatePurchaseEvent(purchaseEvent) || !validateSeller(seller)) return

    const completePurchaseEvent = { ...purchaseEvent, seller: seller }
    const createdLot = await handleCreatePurchaseEvent(completePurchaseEvent)
    if (createdLot) {
      enqueueSnackbar(`${t('taxation.success.createdPe')}`, { variant: 'success' })
      if (onLotCreated) onLotCreated(createdLot)
    } else enqueueSnackbar(`${t('taxation.errors.errorSavingPe')}`, { variant: 'error' })
  }

  // TODO: used only for manual tests
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     setPurchaseEvent({
  //       lotNumber: '147852369',
  //       quantity: 20,
  //       price: 20,
  //       purchaseDate: new Date(),
  //       withVat: false,
  //       currency: APP_CURRENCY[0].code,
  //     } as PurchaseEventPayload)

  //     setSeller({
  //       name: 'qwertyuiop',
  //       addressLine1: '',
  //       addressLine2: '',
  //       city: '',
  //       province: '',
  //       zipCode: '',
  //       country: '',
  //       vatNumber: '7145',
  //     } as SellerPayload)
  //   }
  // }, [])

  const handleReceiptDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return
    setReceiptFile(acceptedFiles[0])
  }

  return (
    <div className="w-100 d-flex flex-column justify-content-between align-items-start">
      <div className="w-100 m-3" style={{ paddingRight: 24 }}>
        {/* Receipt upload */}
        <div className="d-flex mb-3" style={{ gap: 24 }}>
          <div style={{ flex: '0 0 40%' }}>
            <h4 className="mt-0">{t('taxation.lotInformation.receipt')}</h4>
            <p>{t('taxation.receiptUploadHint')}</p>
          </div>
          <div style={{ flex: '1 1 60%' }}>
            <Dropzone
              onDrop={handleReceiptDrop}
              multiple={false}
              accept={['.pdf', '.png', '.jpg', '.jpeg']}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                // @ts-ignore
                <div
                  {...getRootProps()}
                  style={{
                    border: '2px dashed #aaa',
                    borderRadius: 8,
                    padding: 24,
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: isDragActive ? '#f0f0f0' : 'transparent',
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <input {...getInputProps()} />
                  {receiptFile ? (
                    <p className="m-0">
                      <strong>{t('taxation.selectedFile')}</strong> {receiptFile.name}
                    </p>
                  ) : isDragActive ? (
                    <p className="m-0">{t('taxation.dropFileHere')}</p>
                  ) : (
                    <p className="m-0">{t('taxation.dragHere')}</p>
                  )}
                </div>
              )}
            </Dropzone>
            {receiptFile && (
              <div className="mt-2 d-flex justify-content-end">
                <Button variant="outlined" onClick={() => setReceiptFile(null)}>
                  {t('common.remove')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Purchase Date | Lot Number | Quantity | Price */}
        <h4>{t('taxation.lotInformation.lotInformation')}</h4>
        <Grid container spacing={3}>
          <Grid item sm={12} lg={3}>
            <TextField
              label={t('taxation.lotInformation.purchaseDate')}
              fullWidth
              required
              type="date"
              value={formatDateToYYYYMMDD(purchaseEvent.purchaseDate)}
              onChange={(e) => {
                const next = new Date(e.target.value)
                if (isNaN(next.getTime())) return
                setPurchaseEvent((prev) => ({ ...prev, purchaseDate: next }))
              }}
            />
          </Grid>

          <Grid item sm={12} lg={4}>
            <TextField
              fullWidth
              label={t('taxation.lotInformation.lotNumber')}
              name="lot-number"
              value={purchaseEvent.lotNumber}
              required
              error={!!purchaseErrors.lotNumber}
              helperText={purchaseErrors.lotNumber}
              onBlur={(e) => {
                const error = validateSingleLotField('lotNumber', e.target.value)
                setPurchaseErrors((prev) => {
                  const updated = { ...prev }
                  if (error) updated.lotNumber = error
                  else delete updated.lotNumber
                  return updated
                })
              }}
              onChange={(e) => updatePurchasesEvent('lotNumber', e.target.value)}
            />
          </Grid>

          <Grid item sm={12} lg={2}>
            <TextField
              fullWidth
              type="number"
              name="pe-quantity"
              label={t('taxation.lotInformation.totalQuantity')}
              value={purchaseEvent.quantity ?? ''}
              onChange={(e) => {
                if (e.target.value === '') {
                  setPurchaseEvent((prev) => ({ ...prev, quantity: undefined }))
                } else {
                  updatePurchasesEvent('quantity', Number(e.target.value))
                }
              }}
            />
          </Grid>

          <Grid item sm={12} lg={3}>
            <TextField
              fullWidth
              type="number"
              name="pe-price"
              label={t('taxation.lotInformation.totalPrice')}
              error={!!purchaseErrors.price}
              helperText={purchaseErrors.price}
              value={purchaseEvent.price ?? ''}
              onBlur={(e) => {
                const val = e.target.value === '' ? undefined : Number(e.target.value)
                const error = validateSingleLotField('price', val as any)
                setPurchaseErrors((prev) => {
                  const updated = { ...prev }
                  if (error) updated.price = error
                  else delete updated.price
                  return updated
                })
              }}
              onChange={(e) => {
                if (e.target.value === '') {
                  setPurchaseEvent((prev) => ({ ...prev, price: undefined }))
                } else {
                  updatePurchasesEvent('price', Number(e.target.value))
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Currency | VAT toggle */}
        <Grid container spacing={3} className="mt-2">
          <Grid item sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>{t('taxation.lotInformation.currency')}</InputLabel>
              <Select
                value={purchaseEvent.currency}
                onChange={(e) =>
                  setPurchaseEvent({
                    ...purchaseEvent,
                    currency: e.target.value as string,
                  })
                }
              >
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item sm={12} lg={10} className="d-flex align-items-center">
            <ToggleButtonGroup
              value={purchaseEvent.withVat ? 'commercial' : 'private'}
              exclusive
              onChange={(_e, value) => {
                if (value !== null) {
                  setPurchaseEvent((prev) => ({ ...prev, withVat: value === 'commercial' }))
                }
              }}
              size="small"
            >
              <ToggleButton value="private">
                {t('taxation.lotInformation.privateSale')}
              </ToggleButton>
              <ToggleButton value="commercial">
                {t('taxation.lotInformation.commercialSale')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>

        {/* Seller info */}
        <div className="mt-4">
          <h4>{t('taxation.seller.sellerInfo')}</h4>
          <p>{t('taxation.seller.provideSellerInfo')}</p>
          <Grid container spacing={3}>
            <Grid item sm={12} lg={6}>
              <TextField
                label={t('taxation.seller.sellerName')}
                name="seller-name"
                value={seller.name}
                fullWidth
                error={!!sellerErrors.name}
                helperText={sellerErrors.name}
                onBlur={(e) => {
                  const error = validateSingleSellerField('name', e.target.value)
                  setSellerErrors((prev) => {
                    const updated = { ...prev }
                    if (error) updated.name = error
                    else delete updated.name
                    return updated
                  })
                }}
                onChange={(e) => updateSeller('name', e.target.value)}
              />
            </Grid>

            <Grid item sm={12} lg={6}>
              <TextField
                fullWidth
                name="vat-number"
                label={t('taxation.seller.vatNumber')}
                value={seller.vatNumber}
                error={!!sellerErrors.vatNumber}
                helperText={sellerErrors.vatNumber}
                onBlur={(e) => {
                  const error = validateSingleSellerField('vatNumber', e.target.value)
                  setSellerErrors((prev) => {
                    const updated = { ...prev }
                    if (error) updated.vatNumber = error
                    else delete updated.vatNumber
                    return updated
                  })
                }}
                onChange={(e) => updateSeller('vatNumber', e.target.value)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item sm={12} lg={6}>
              <TextField
                label={t('taxation.seller.addressLine1')}
                name="address-line"
                value={seller.addressLine1}
                fullWidth
                onChange={(e) => updateSeller('addressLine1', e.target.value)}
              />
            </Grid>

            <Grid item sm={12} lg={6}>
              <TextField
                label={t('taxation.seller.addressLine2')}
                fullWidth
                value={seller.addressLine2}
                onChange={(e) => updateSeller('addressLine2', e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item sm={6} lg={2}>
              <TextField
                label={t('taxation.seller.zipCode')}
                name="zip-code"
                fullWidth
                value={seller.zipCode}
                onChange={(e) => updateSeller('zipCode', e.target.value)}
              />
            </Grid>

            <Grid item sm={6} lg={2}>
              <TextField
                label={t('taxation.seller.country')}
                name="country"
                fullWidth
                value={seller.country}
                onChange={(e) => updateSeller('country', e.target.value)}
              />
            </Grid>

            <Grid item sm={6} lg={4}>
              <TextField
                label={t('taxation.seller.city')}
                name="city"
                fullWidth
                value={seller.city}
                onChange={(e) => updateSeller('city', e.target.value)}
              />
            </Grid>

            <Grid item sm={6} lg={4}>
              <TextField
                label={t('taxation.seller.province')}
                name="province"
                fullWidth
                value={seller.province}
                onChange={(e) => updateSeller('province', e.target.value)}
              />
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="w-100 d-flex justify-content-end">
        <Button variant="contained" onClick={handleSaveLot} data-testid="save-purchase-event">
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
